import {
  type CheckoutSession,
  type Store,
  CheckoutStatus,
  CHECKOUT_STATUS_PROCESSING_PAYMENT,
} from "@/types";
import * as lib3ds from "@/lib/3ds";
import { wait } from "@/lib/common";
import { withCheckoutId, toAppPath } from "@/lib/checkout-id";
import { logInfo, logDebug } from "@/services/logging/logger";

export function shouldPoll(
  session: CheckoutSession | undefined,
): boolean {
  if (!session) return false;
  return CHECKOUT_STATUS_PROCESSING_PAYMENT.includes(
    session.status ?? CheckoutStatus.UNKNOWN,
  );
}

export async function pollPaymentStatus(
  store: Store,
  getCheckoutSession: () => Promise<CheckoutSession>,
  onSessionFetched: (session: CheckoutSession) => void,
  statusCheck: CheckoutStatus,
): Promise<void> {
  logInfo("payment-polling", "GET request triggered to checkout/v3");
  const session = await getCheckoutSession();
  logInfo("payment-polling", "GET response received from checkout/v3", {
    sessionId: session.id,
    status: session.status,
  });

  const check3dsPassed =
    (await lib3ds.get3dsResult(store)) === lib3ds.Result.OK;
  const wasRedirectInitiated = await lib3ds.is3dsRedirectInitiated(store);
  const isCompleted = await lib3ds.is3dsCompleted(store);
  const redirectUrl = session.paymentResult.redirectUrl ?? "";
  const willRedirect =
    redirectUrl !== "" &&
    !check3dsPassed &&
    !wasRedirectInitiated &&
    !isCompleted;

  logInfo("payment-polling", "3DS redirect URL check completed", {
    sessionId: session.id,
    redirectUrl: redirectUrl || "(empty)",
    check3dsPassed,
    wasRedirectInitiated,
    isCompleted,
    willRedirect,
    status: session.status,
  });

  if (wasRedirectInitiated && !check3dsPassed) {
    logInfo(
      "payment-polling",
      "User returned from 3DS, marking as complete",
      { sessionId: session.id, status: session.status },
    );

    await lib3ds.set3dsCompletedFlag(store);
    await lib3ds.clear3dsRedirectInitiated(store);

    const currentResult = await lib3ds.get3dsResult(store);
    if (currentResult === lib3ds.Result.NONE) {
      await store.set("3ds_result", "ok");
    }
  }

  const isOn3dsReturnPath = window.location.pathname.startsWith(
    toAppPath("/3ds"),
  );

  if (willRedirect && !isOn3dsReturnPath) {
    logInfo("payment-polling", "3DS redirect needed - navigating to /3ds", {
      sessionId: session.id,
      redirectUrl: redirectUrl || "(empty)",
      status: session.status,
    });
    try {
      sessionStorage.setItem(
        `cosmos_3ds_redirect_${session.id}`,
        redirectUrl,
      );
    } catch {
      // sessionStorage might be blocked
    }
    window.location.href = toAppPath(withCheckoutId("/3ds"));
    return;
  }

  onSessionFetched(session);

  if (!shouldPoll(session)) {
    logDebug("payment-polling", "Polling stopped", {
      sessionId: session.id,
      status: session.status,
      statusCheck,
      shouldPoll: shouldPoll(session),
      statusChanged: statusCheck !== session.status,
    });
    return;
  }

  await wait(3000);
  return await pollPaymentStatus(
    store,
    getCheckoutSession,
    onSessionFetched,
    statusCheck,
  );
}
